"use client";

import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Upload, File, Image, FileText, X, Check, Clock } from "lucide-react";
import { cn } from "@repo/lib";
import { toast } from "./ui";

interface UploadedFile {
  id: string;
  documentId: string;
  storageUrl: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  scanStatus: "pending" | "clean";
  uploadedAt: string;
}

const FileUploader = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      
      return response.json();
    },
    onSuccess: (data, file) => {
      const newFile: UploadedFile = {
        id: crypto.randomUUID(),
        documentId: data.documentId,
        storageUrl: data.storageUrl,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        scanStatus: "pending",
        uploadedAt: new Date().toISOString(),
      };
      
      setFiles(prev => [...prev, newFile]);
      
      // Simulate scan completion after 2 seconds
      setTimeout(() => {
        setFiles(prev => 
          prev.map(f => 
            f.id === newFile.id 
              ? { ...f, scanStatus: "clean" as const }
              : f
          )
        );
      }, 2000);
      
      toast(`${file.name} has been uploaded and is being scanned.`);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : "Something went wrong");
    },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    onDrop: (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        uploadMutation.mutate(file);
      });
    },
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((rejection) => {
        toast.error(`${rejection.file.name}: ${rejection.errors[0]?.message || 'Unknown error'}`);
      });
    },
  });

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith("image/")) {
      return <Image className="h-4 w-4" />;
    }
    if (fileType === "application/pdf") {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  const getScanStatusBadge = (status: "pending" | "clean") => {
    if (status === "pending") {
      return (
        <Badge variant="secondary" className="animate-pulse">
          <Clock className="h-3 w-3 mr-1" />
          Scanning
        </Badge>
      );
    }
    return (
      <Badge variant="default" className="bg-green-500 hover:bg-green-600">
        <Check className="h-3 w-3 mr-1" />
        Clean
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>File Upload</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
              isDragActive && "border-primary bg-primary/5",
              "border-muted-foreground/25 hover:border-muted-foreground/50"
            )}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <div className="space-y-2">
              <p className="text-lg font-medium">
                {isDragActive
                  ? "Drop the files here..."
                  : "Drag & drop files here, or click to select"}
              </p>
              <p className="text-sm text-muted-foreground">
                Supports PDF and image files (PNG, JPG, GIF, WebP) up to 10MB
              </p>
            </div>
          </div>
          
          {uploadMutation.isPending && (
            <div className="mt-4 text-center">
              <p className="text-sm text-muted-foreground">Uploading...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {files.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Uploaded Files ({files.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getFileIcon(file.fileType)}
                        <span className="font-medium">{file.fileName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {file.fileType}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatFileSize(file.fileSize)}
                    </TableCell>
                    <TableCell>
                      {getScanStatusBadge(file.scanStatus)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(file.uploadedAt).toLocaleTimeString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(file.id)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FileUploader;
